import { DisclosureError, BaseQuestionError } from "../types/InterviewError";

export const setBaseQuestionError = (
  errors: BaseQuestionError[],
  error: BaseQuestionError,
) => {
  if (
    errors.filter((e: BaseQuestionError) => e.code === error.code).length === 0
  ) {
    errors.push(error);
  }
  return errors;
};

export const clearBaseQuestionError = (
  errors: BaseQuestionError[],
  code: string,
) => {
  return errors.filter((e: BaseQuestionError) => e.code !== code);
};

/** Below functions handle setting errors with error messages on disclosures.
 *
 *  This is a little more complex than base questions, as a single disclosure
 *  might contain several different questions that all need to handle a potential
 *  error. We use the aliasName of a disclosure to identify the disclosure, and keep
 *  track of individual question errors from their index in an attached error array.
 */

export const setDisclosureError = (
  disclosureErrors: DisclosureError[],
  error: {
    aliasName: string;
    error: { errorIndex: number; errorMessage: string };
  },
) => {
  const { aliasName } = error;

  // Check for existing error.
  const currentError = disclosureErrors.find(
    (existingError) => existingError.aliasName === aliasName,
  );

  // Add error.
  if (currentError && currentError.errors.length > 0) {
    // Update existing error array with new error.
    const updatedError = [...currentError.errors, error.error];
    // Merge new error into existing error list.
    return disclosureErrors.map((err) => {
      if (err.aliasName === aliasName) {
        return { ...err, errors: updatedError };
      }
      return err;
    });
  } else {
    // Add new error.
    return [
      ...disclosureErrors,
      { aliasName: aliasName, errors: [error.error] },
    ];
  }
};

export const clearDisclosureError = (
  disclosureErrors: DisclosureError[],
  submittedDisclosureIndex: number,
  aliasName: string,
) => {
  // Check for existing error.
  const currentDisclosureError = disclosureErrors.find(
    (error) => error.aliasName === aliasName,
  );

  // Clear errors. If disclosure has multiple active errors on the same disclosure,
  // only remove the relevant error with matching index.
  if (currentDisclosureError) {
    if (currentDisclosureError.errors.length > 1) {
      const filteredErrorsList = currentDisclosureError.errors.filter(
        (error) => error.errorIndex !== submittedDisclosureIndex,
      );

      // Merge updated error to remove cleared.
      return disclosureErrors.map((error) => {
        if (error.aliasName === aliasName) {
          return { ...error, errors: filteredErrorsList };
        }
        return error;
      });
    } else if (
      currentDisclosureError.errors.filter(
        (error) => error.errorIndex !== submittedDisclosureIndex,
      ).length === 0
    ) {
      // If only one error to clear, remove full disclosure error object.
      return disclosureErrors.filter(
        (error: DisclosureError) => error.aliasName !== aliasName,
      );
    }
  }

  return disclosureErrors;
};
